/**
 * The document server, as the editor sees it.
 *
 * Everything here is same-origin `/api/...`: in development Vite proxies that
 * to the server process, and in a deployment a reverse proxy does. Keeping it
 * same-origin means no CORS, no credential mode to get wrong, and no second
 * hostname to configure — the editor never has to know where the server is.
 */

export interface Capabilities {
  /** One server-authoritative capability for every private cloud mutation. */
  cloudWrite: boolean;
}

export interface Features {
  /** Whether this account may use live rooms. False keeps editing local-only. */
  rooms: boolean;
  /** Google appears only when this deployment has OAuth credentials. */
  googleAuth: boolean;
  /** Whether authenticated object-storage previews can be generated. */
  previews: boolean;
}

export interface CloudUser {
  id: string;
  /** Public collaborator identity. Email remains private account data. */
  handle?: string;
  email: string;
  name?: string;
  image?: string | null;
  emailVerified?: boolean;
  hasPassword?: boolean;
  /** New accounts must choose a public collaborator identity before continuing. */
  handleRequired?: boolean;
}

export interface CloudDocumentMeta {
  id: string;
  name: string;
  /** Bumped by the server on every save; the basis of conflict detection. */
  version: number;
  createdAt: number;
  updatedAt: number;
  /** Once true, CRDT rooms are the only cloud writer for this document. */
  roomManaged: boolean;
  /** Template launch hidden from the library until the first real mutation. */
  draft?: boolean;
  /** Effective permission for the current user. */
  access: 'owner' | 'editor' | 'viewer';
  /** Object-storage image served by the authenticated preview endpoint. */
  previewUrl: string | null;
  /** Independent compare-and-swap version for replacing the preview. */
  previewVersion?: number;
}

export interface CloudDocument extends CloudDocumentMeta {
  body: string;
}

export interface CloudShare {
  id: string;
  email: string;
  role: 'viewer' | 'editor';
  createdAt: number;
  updatedAt: number;
}

export interface CloudPublicLink {
  enabled: boolean;
  role: 'viewer' | 'editor';
  createdAt: number | null;
  updatedAt: number | null;
}

export interface CloudPublication {
  id: string;
  name: string;
  publisherHandle: string;
  revision: number;
  bodyHash: string;
  license: { identifier: 'CC0-1.0'; url: string };
  publishedAt: number;
  updatedAt: number;
  upvotes: number;
  previewUrl: string | null;
  previewVersion: number;
  /** Mutable gallery listing copy; not part of the immutable CC0 snapshot. */
  artistMarkdown: string;
  sourceDocumentId: string;
  unpublished: boolean;
  moderated: boolean;
}

export interface CloudSharing {
  shares: CloudShare[];
  link: CloudPublicLink;
}

export interface BillingSummary {
  enabled: boolean;
  customer: boolean;
  entitlement: {
    key: string;
    active: boolean;
    state: 'none' | 'active' | 'grace' | 'expired' | 'inactive';
    validUntil: number | null;
  };
  subscription: {
    status: string;
    cancelAtPeriodEnd: boolean;
    currentPeriodEnd: number | null;
  } | null;
}

/**
 * A save that lost a race.
 *
 * Carries the winning document so the caller can decide what to do rather than
 * simply failing — with one user in two tabs the right answer is usually to
 * reload, and with two users it is the reason phase two exists.
 */
export class CloudConflict extends Error {
  constructor(readonly current: CloudDocument) {
    super('This document changed somewhere else since you last loaded it');
    this.name = 'CloudConflict';
  }
}

export class CloudRoomRequired extends Error {
  constructor(readonly current: CloudDocument) {
    super('Reconnect to the live room to save this document');
    this.name = 'CloudRoomRequired';
  }
}

export class CloudRequestError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
    this.name = 'CloudRequestError';
  }
}

export class CloudUnavailable extends Error {
  constructor(cause?: unknown) {
    super('The document server is not reachable');
    this.name = 'CloudUnavailable';
    this.cause = cause;
  }
}

export async function documentBodyHash(body: string): Promise<string> {
  let canonical = body;
  try {
    canonical = JSON.stringify(JSON.parse(body));
  } catch {
    // Compatibility with legacy opaque document bodies.
  }
  const bytes = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(canonical));
  return [...new Uint8Array(bytes)].map((byte) => byte.toString(16).padStart(2, '0')).join('');
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  let response: Response;
  try {
    response = await fetch(`/api${path}`, {
      ...init,
      headers: { 'content-type': 'application/json', ...(init?.headers ?? {}) },
    });
  } catch (err) {
    // A dead server and a dead network are the same thing to the editor: work
    // offline and stop asking.
    throw new CloudUnavailable(err);
  }

  if (response.status === 204) return undefined as T;

  let payload: Record<string, unknown> = {};
  try {
    payload = (await response.json()) as Record<string, unknown>;
  } catch {
    // Vite answers with HTML when nothing is listening behind the proxy, so a
    // body that will not parse means the server is absent rather than upset.
    if (!response.ok) throw new CloudUnavailable(response.status);
  }

  // The development proxy answers this way when the server process is not
  // running, so an absent server stays distinguishable from a signed-out one.
  if (payload.unavailable === true) throw new CloudUnavailable('not running');

  if (response.status === 409 && payload.document) {
    if (payload.code === 'room_authoritative') {
      throw new CloudRoomRequired(payload.document as CloudDocument);
    }
    throw new CloudConflict(payload.document as CloudDocument);
  }
  if (!response.ok) {
    throw new CloudRequestError(
      String(payload.message ?? payload.error ?? `Request failed (${response.status})`),
      response.status,
    );
  }
  return payload as T;
}

function followOAuthContinuation(result: { redirect?: boolean; url?: string }): void {
  if (!result.redirect || typeof result.url !== 'string') return;
  const target = new URL(result.url, window.location.origin);
  // Login continuation may go to KilnCat's consent screen or to the client's registered callback.
  // Better Auth signed and validated the flow; disallow non-HTTP(S) schemes before navigation.
  if (target.protocol !== 'http:' && target.protocol !== 'https:') {
    throw new Error('OAuth continuation returned an unsafe URL');
  }
  window.location.assign(target);
}

export const cloud = {
  async signIn(email: string, password: string, oauthQuery?: string): Promise<CloudUser> {
    const result = await request<{ redirect?: boolean; url?: string }>('/auth/sign-in/email', {
      method: 'POST',
      body: JSON.stringify({ email, password, ...(oauthQuery ? { oauth_query: oauthQuery } : {}) }),
    });
    followOAuthContinuation(result);
    const identity = await request<{ user: CloudUser | null }>('/me');
    if (!identity.user) throw new Error('Sign-in did not create a session');
    return identity.user;
  },

  async signUp(email: string, password: string, oauthQuery?: string): Promise<CloudUser> {
    const result = await request<{ redirect?: boolean; url?: string }>('/auth/sign-up/email', {
      method: 'POST',
      body: JSON.stringify({
        email,
        password,
        name: email.slice(0, email.indexOf('@')) || email,
        callbackURL: '/documents',
        ...(oauthQuery ? { oauth_query: oauthQuery } : {}),
      }),
    });
    followOAuthContinuation(result);
    const identity = await request<{ user: CloudUser | null }>('/me');
    if (!identity.user) {
      throw new Error('Check your email for a verification link, then sign in.');
    }
    return identity.user;
  },

  async signOut(): Promise<void> {
    await request('/auth/sign-out', { method: 'POST', body: '{}' });
  },

  /** Begin Better Auth's Google redirect while keeping the final destination same-origin. */
  async googleSignIn(callbackURL = '/documents', oauthQuery?: string): Promise<void> {
    const { url } = await request<{ url: string }>('/auth/sign-in/social', {
      method: 'POST',
      body: JSON.stringify({
        provider: 'google',
        callbackURL,
        disableRedirect: true,
        ...(oauthQuery ? { oauth_query: oauthQuery } : {}),
      }),
    });
    if (!url) throw new Error('Google sign-in is not configured');
    window.location.assign(url);
  },

  /**
   * Who we are, and what this deployment currently offers.
   *
   * Features arrive with identity rather than from a build, so multiplayer can
   * be turned off for everyone without shipping anything to a browser.
   */
  async me(): Promise<{
    user: CloudUser | null;
    features: Features;
    capabilities: Capabilities;
  }> {
    const payload = await request<{
      user: CloudUser | null;
      features?: Partial<Features>;
      capabilities?: Partial<Capabilities>;
    }>('/me');
    return {
      user: payload.user ?? null,
      // An older server that says nothing about features is assumed to have
      // them: absence should not silently disable something that works.
      features: {
        rooms: payload.features?.rooms ?? true,
        googleAuth: payload.features?.googleAuth ?? false,
        previews: payload.features?.previews ?? false,
      },
      capabilities: {
        cloudWrite: payload.capabilities?.cloudWrite ?? true,
      },
    };
  },

  async updateAccount(patch: { handle?: string; image?: string | null }): Promise<CloudUser> {
    const { user } = await request<{ user: CloudUser }>('/me', {
      method: 'PATCH',
      body: JSON.stringify(patch),
    });
    return user;
  },

  async recoverAccount(
    handle: string,
    kind: 'forgot-email' | 'reset-password' = 'forgot-email',
  ): Promise<string> {
    const result = await request<{ message: string }>('/account/recover', {
      method: 'POST',
      body: JSON.stringify({ handle, kind }),
    });
    return result.message;
  },

  async changeEmail(newEmail: string): Promise<void> {
    await request('/auth/change-email', {
      method: 'POST',
      body: JSON.stringify({ newEmail, callbackURL: '/documents' }),
    });
  },

  async changePassword(currentPassword: string, newPassword: string): Promise<void> {
    await request('/auth/change-password', {
      method: 'POST',
      body: JSON.stringify({ currentPassword, newPassword, revokeOtherSessions: true }),
    });
  },

  async sendVerificationEmail(email: string): Promise<void> {
    await request('/auth/send-verification-email', {
      method: 'POST',
      body: JSON.stringify({ email, callbackURL: '/documents' }),
    });
  },

  async billing(): Promise<BillingSummary> {
    try {
      return await request<BillingSummary>('/billing');
    } catch {
      // Billing is optional deployment metadata. An older or temporarily
      // degraded billing route must never block document sync or local editing.
      return {
        enabled: false,
        customer: false,
        entitlement: { key: 'pro', active: false, state: 'none', validUntil: null },
        subscription: null,
      };
    }
  },

  async checkout(): Promise<string> {
    const { url } = await request<{ url: string }>('/billing/checkout', {
      method: 'POST',
      body: '{}',
    });
    return url;
  },

  async portal(): Promise<string> {
    const { url } = await request<{ url: string }>('/billing/portal', {
      method: 'POST',
      body: '{}',
    });
    return url;
  },

  async list(): Promise<CloudDocumentMeta[]> {
    const { documents } = await request<{ documents: CloudDocumentMeta[] }>('/documents');
    return documents;
  },

  async create(
    name: string,
    body: string,
    options: { draft?: boolean } = {},
  ): Promise<CloudDocumentMeta> {
    const { document } = await request<{ document: CloudDocumentMeta }>('/documents', {
      method: 'POST',
      body: JSON.stringify({ name, body, draft: options.draft === true }),
    });
    return document;
  },

  async publish(id: string): Promise<CloudDocumentMeta> {
    const { document } = await request<{ document: CloudDocumentMeta }>(
      `/documents/${id}/publish`,
      { method: 'POST', body: '{}' },
    );
    return document;
  },

  async open(id: string): Promise<CloudDocument> {
    const { document } = await request<{ document: CloudDocument }>(`/documents/${id}`);
    return document;
  },

  /** Saving carries the name too, so the title in the editor is the document's name. */
  async save(id: string, body: string, version: number, name?: string): Promise<CloudDocumentMeta> {
    const { document } = await request<{ document: CloudDocumentMeta }>(`/documents/${id}`, {
      method: 'PUT',
      body: JSON.stringify({ body, version, name }),
    });
    return document;
  },

  async rename(id: string, name: string): Promise<CloudDocumentMeta> {
    const { document } = await request<{ document: CloudDocumentMeta }>(`/documents/${id}`, {
      method: 'PATCH',
      body: JSON.stringify({ name }),
    });
    return document;
  },

  async remove(id: string): Promise<void> {
    await request(`/documents/${id}`, { method: 'DELETE' });
  },

  async publication(id: string): Promise<CloudPublication | null> {
    const result = await request<{ publication: CloudPublication | null }>(
      `/documents/${encodeURIComponent(id)}/publication`,
    );
    return result.publication;
  },

  async publishCc0(id: string, artistMarkdown?: string): Promise<CloudPublication> {
    const result = await request<{ publication: CloudPublication }>(
      `/documents/${encodeURIComponent(id)}/publication`,
      {
        method: 'POST',
        body: JSON.stringify({
          acceptCc0: true,
          ...(artistMarkdown === undefined ? {} : { artistMarkdown }),
        }),
      },
    );
    return result.publication;
  },

  async unpublish(publicationId: string): Promise<void> {
    await request(`/publications/${encodeURIComponent(publicationId)}`, { method: 'DELETE' });
  },

  async sharing(id: string): Promise<CloudSharing> {
    return request<CloudSharing>(`/documents/${id}/sharing`);
  },

  async shareWithEmail(id: string, email: string, role: 'viewer' | 'editor'): Promise<CloudShare> {
    const { share } = await request<{ share: CloudShare }>(`/documents/${id}/sharing`, {
      method: 'POST',
      body: JSON.stringify({ email, role }),
    });
    return share;
  },

  async updateEmailShare(
    id: string,
    shareId: string,
    role: 'viewer' | 'editor',
  ): Promise<CloudShare> {
    const { share } = await request<{ share: CloudShare }>(
      `/documents/${id}/sharing/email/${shareId}`,
      { method: 'PATCH', body: JSON.stringify({ role }) },
    );
    return share;
  },

  async removeEmailShare(id: string, shareId: string): Promise<void> {
    await request(`/documents/${id}/sharing/email/${shareId}`, { method: 'DELETE' });
  },

  async setPublicLink(
    id: string,
    role: 'viewer' | 'editor',
    rotate = false,
  ): Promise<{ link: CloudPublicLink; url: string | null }> {
    return request(`/documents/${id}/sharing/link`, {
      method: 'PUT',
      body: JSON.stringify({ role, rotate }),
    });
  },

  async disablePublicLink(id: string): Promise<void> {
    await request(`/documents/${id}/sharing/link`, { method: 'DELETE' });
  },

  async redeemPublicLink(id: string, token: string): Promise<CloudDocumentMeta> {
    const { document } = await request<{ document: CloudDocumentMeta }>(
      `/documents/${id}/share/redeem`,
      { method: 'POST', body: JSON.stringify({ token }) },
    );
    return document;
  },
};
